<template>
  <Teleport to="body" v-if="show">
    <div class="modal-overlay">
      <div class="modal-container">

          <div class="modal-header">
            <h5>Reagendar Aula de Reposição</h5>
            <button type="button" class="btn-close" aria-label="Fechar" @click="emitClose()"></button>
          </div>

          <div class="modal-content">
            <div v-if="aula">
              <div><strong>Aluno:</strong> {{ aula.aluno_nome || '—' }}</div>
              <div><strong>Data atual:</strong> {{ fmtDate(aula.data_aula) }}</div>
            </div><p></p>

            <div class="row g-2">
              <div class="form-group">
                <label>Nível <span class="required">*</span></label>
                <select class="form-select form-select-sm" v-model="nivelId" :disabled="loading || isSubmitting">
                  <option :value="null">Selecione...</option>
                  <option v-for="n in niveis" :key="n.id" :value="String(n.id)">
                    {{ n.nivel }}
                  </option>
                </select>
              </div>

              <div class="form-group">
                <label>Turma <span class="required">*</span></label>
                <select class="form-select form-select-sm" v-model="turmaKey" :disabled="loading || !nivelId || isSubmitting">
                  <option :value="null">Selecione...</option>
                  <option
                    v-for="t in turmasDisponiveis"
                    :key="`${t.id}-${t.dia}-${t.horario}`"
                    :value="`${t.id}|||${t.dia}|||${t.horario}`"
                  >
                    {{ t.id }} | {{ t.instrutor }} | {{ t.dia }} | {{ String(t.horario || '').slice(0, 5) }}
                  </option>
                </select>
              </div>

              <div class="col-md-4 form-group">
                <label>Nova data <span class="required">*</span></label>
                <FlatPickr
                  v-model="novaData"
                  :config="dateConfig"
                  class="form-control form-control-sm"
                  :disabled="loading || !turmaSelecionada || isSubmitting"
                />
              </div>

              <div class="form-group">
                <label>Observações</label>
                <textarea class="form-control" rows="2" v-model="observacoes" :disabled="isSubmitting" placeholder="(opcional)"></textarea>
              </div>
            </div>

            <div v-if="errorMsg" class="alert alert-danger py-2 mt-3 mb-0 small">
              {{ errorMsg }}
            </div>
          </div>

          <!-- Rodapé (Ações) -->
          <div class="d-flex justify-content-end mb-4">
            <button 
              class="btn btn-secondary me-2" 
              @click="emitClose" 
              :disabled="isSubmitting"
            >
              Cancelar
            </button>
            <button
              :class="['btn', isSubmitting ? 'btn-success' : 'btn-primary']"
              :disabled="!formValid || isSubmitting"
              @click="salvar"
            >
              {{ isSubmitting ? 'Salvando…' : 'Salvar' }}
            </button>
          </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance } from 'vue'
import axios from '@/services/axios'
import FlatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import { Portuguese } from 'flatpickr/dist/l10n/pt.js'


// pega o nome do usuário do globalState (padrão do seu app)
const { appContext } = getCurrentInstance()
const userNameFromGlobal = appContext?.config?.globalProperties?.$globalState?.userName || null

const lastHydratedId = ref(null) // evita re-hidratações desnecessárias na mesma aula

const props = defineProps({
  show: { type: Boolean, default: false },
  // esperado: { id, aluno_nome, nivel_id, turma_id, dia_semana, horario, turma_completa, data_aula }
  aula: { type: Object, default: null }
})
const emit = defineEmits(['close', 'saved'])

const loading = ref(false)
const isSubmitting = ref(false)
const errorMsg = ref('')

// === ESTADOS ===
const niveis = ref([])               // [{ id, nivel, ativo }, ...]
const turmasDisponiveis = ref([])    // [{ id, dia, horario, instrutor, nivel }, ...]

const nivelId = ref(null)            // string
const turmaKey = ref(null)           // "id|||dia|||horario"

const novaData = ref('')
const observacoes = ref('')

// helpers de ordenação (JS puro)
const idxDia = (d) => {
  let s = String(d || '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/-?feira/g, '')
    .trim();
  s = s.split(/\s+/)[0].slice(0, 3); // seg, ter, qua, qui, sex, sab, dom
  const mapa = { seg: 1, ter: 2, qua: 3, qui: 4, sex: 5, sab: 6, dom: 7 };
  return mapa[s] ?? 99;
};

const toMin = (h) => {
  const m = String(h || '').match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (!m) return Number.MAX_SAFE_INTEGER;
  return (+m[1]) * 60 + (+m[2]);
};

// ===== helpers =====

async function abrirECarregar() {
  loading.value = true
  errorMsg.value = ''
  try {

    // estado inicial
    nivelId.value   = props.aula?.nivel_id != null ? String(props.aula.nivel_id) : null
    turmaKey.value  = null
    novaData.value  = ''
    observacoes.value = ''

    await carregarNiveis()
    if (!nivelId.value && props.aula?.nivel_id != null) {
      nivelId.value = String(props.aula.nivel_id)
    }

    await carregarTurmasDisponiveis()

    if (props.aula?.turma_id && props.aula?.dia_semana && props.aula?.horario) {
      turmaKey.value = `${props.aula.turma_id}|||${props.aula.dia_semana}|||${(props.aula.horario||'').trim()}`
    }

  } catch (e) {
    console.error('Erro ao abrir modal de reagendamento:', e)
    errorMsg.value = 'Falha ao carregar dados.'
  } finally {
    loading.value = false
  }
}


function emitClose(payload) { emit('close', payload || { saved: false }) }

function convertDayNameToNumber(dayName) {
  const dias = {
    'domingo':0, 'segunda-feira':1, 'terça-feira':2, 'quarta-feira':3,
    'quinta-feira':4, 'sexta-feira':5, 'sábado':6
  }
  return dias[(dayName||'').toLowerCase()] ?? null
}
// function isoYMD(v) {
//   if (!v) return ''
//   const d = new Date(v); if (isNaN(d)) return ''
//   const mm = String(d.getMonth()+1).padStart(2,'0')
//   const dd = String(d.getDate()).padStart(2,'0')
//   return `${d.getFullYear()}-${mm}-${dd}`
// }
function fmtDate(v) {
  if (!v) return '—'
  const d = new Date(v); if (isNaN(d)) return v
  const dd = String(d.getDate()).padStart(2,'0')
  const mm = String(d.getMonth()+1).padStart(2,'0')
  return `${dd}/${mm}/${d.getFullYear()}`
}

// ===== CARREGADORES (mesma lógica do Formulário) =====
async function carregarNiveis() {
  const response = await axios.get(`/cadastros/niveis-turmas`)
  const dados = response.data?.data || response.data || []
  niveis.value = Array.isArray(dados) ? dados.filter(n => n.ativo) : []
}

function extrairInstrutorDaTurmaCompleta(txt) {
  const s = String(txt || '')
  // procura "Instrutor: NOME"
  const m = s.match(/Instrutor:\s*(.+)$/i)
  return m?.[1]?.trim() || null
}

function instrutorFallbackParaTurma(turmaId) {
  // só usa fallback se for a turma atual da aula
  if (!props.aula) return null
  if (String(props.aula.turma_id) !== String(turmaId)) return null
  return (
    props.aula.instrutor_nome ||
    extrairInstrutorDaTurmaCompleta(props.aula.turma_completa) ||
    null
  )
}


async function carregarTurmasDisponiveis() {
  turmasDisponiveis.value = []
  if (!nivelId.value) return

  // Carrega listas
  const [respDH, respT] = await Promise.all([
    axios.get(`/cadastros/dias-horarios-turmas`),
    axios.get(`/cadastros/turmas`)
  ])
  const dh = respDH.data?.data || respDH.data || []
  const turmas = respT.data?.data || respT.data || []

  // Nível escolhido
  const nivelSel = niveis.value.find(n => String(n.id) === String(nivelId.value))
  if (!nivelSel) { turmasDisponiveis.value = []; return }

  // Mapa turma_id -> nivel_id (quando vier só em /turmas)
  const turmaNivelMap = Object.create(null)
  for (const t of turmas) {
    if (t?.id != null && t?.nivel_id != null) {
      turmaNivelMap[String(t.id)] = String(t.nivel_id)
    }
  }

  // Preferimos filtrar por ID. Se não vier no DH, caímos para o nome
  const alvoNivelId = String(nivelSel.id)
  const alvoNivelNome = (nivelSel.nivel || '').toLowerCase()

  const lista = dh
    .filter(t => {
      const nivelIdDH   = t?.nivel_id != null ? String(t.nivel_id) : null
      const nivelNomeDH = (t?.nivel_nome || '').toLowerCase()
      const nivelViaTurma = turmaNivelMap[String(t?.turma_id)] || null

      // 1) se DH tem nivel_id, use-o
      if (nivelIdDH) return nivelIdDH === alvoNivelId
      // 2) senão, se temos via turma, use-o
      if (nivelViaTurma) return nivelViaTurma === alvoNivelId
      // 3) último recurso: comparar por nome
      return nivelNomeDH && nivelNomeDH === alvoNivelNome
    })
    .map(t => {
      const turmaInfo = turmas.find(x => String(x.id) === String(t.turma_id)) || {};
      return {
        id: t.turma_id,
        dia: t.dia_semana,                  // ex.: "terça-feira"
        horario: (t.horario || '').trim(),  // ex.: "10:00:00"
        instrutor: turmaInfo.instrutor_nome
          || instrutorFallbackParaTurma(t.turma_id)
          || 'Não informado',
        nivel: t.nivel_nome || nivelSel.nivel,
        nivel_id: Number(t.nivel_id ?? turmaNivelMap[String(t.turma_id)] ?? nivelSel.id)
      };
    })
    .sort((a, b) =>
      (Number(a.id) - Number(b.id)) ||                 // 1) ID da turma (crescente)
      (idxDia(a.dia) - idxDia(b.dia)) ||               // 2) Dia (Seg→Dom)
      (toMin(a.horario) - toMin(b.horario))            // 3) Horário (crescente)
    );

  // Garante que a turma atual (props.aula) apareça mesmo se filtrada/inativa
  turmasDisponiveis.value = garantirTurmaAtualNaLista(lista)
}

function garantirTurmaAtualNaLista(lista) {
  const tId = props.aula?.turma_id
  const dia = (props.aula?.dia_semana || '').toLowerCase()
  const hr  = (props.aula?.horario || '').trim()

  if (!tId || !dia || !hr) return lista

  const exists = lista.some(t =>
    String(t.id) === String(tId) &&
    (t.dia || '').toLowerCase() === dia &&
    (t.horario || '').trim() === hr
  )
  if (exists) return lista

  // tenta obter dados da turma para preencher instrutor/nivel
  const turmaFallback = {
    id: tId,
    dia: props.aula?.dia_semana,
    horario: hr,
    instrutor: instrutorFallbackParaTurma(tId) || 'Não informado',
    nivel: (niveis.value.find(n => String(n.id) === String(nivelId.value))?.nivel) || '—',
    nivel_id: Number(nivelId.value) || null,
    _injetada: true // marca para debug, se quiser
  }
  return [...lista, turmaFallback]
}


// ===== WATCHES =====
watch(() => props.show, (v) => {
  if (!v) {
    // limpa estado para próxima abertura
    lastHydratedId.value = null
    loading.value = false
    isSubmitting.value = false
    errorMsg.value = ''
    nivelId.value = null
    turmaKey.value = null
    turmasDisponiveis.value = []
    novaData.value = ''
    observacoes.value = ''
  }
})

watch(
  () => [props.show, props.aula?.id],
  async ([show, id]) => {
    if (!show || !id) return
    if (lastHydratedId.value === id) return // evita reprocessar mesma aula
    lastHydratedId.value = id
    await abrirECarregar()
  },
  { immediate: true }
)

watch(nivelId, async () => {
  // Durante a abertura/hidratação inicial, não limpe a data
  if (loading.value) {
    await carregarTurmasDisponiveis()
    return
  }

  // Aqui sim é mudança do usuário → limpe e recarregue
  turmaKey.value = null
  novaData.value = ''
  await carregarTurmasDisponiveis()
})

// Turma selecionada a partir da chave "id|||dia|||horario"
const turmaSelecionada = computed(() => {
  if (!turmaKey.value) return null
  const [id, dia, horario] = turmaKey.value.split('|||')
  return turmasDisponiveis.value.find(t =>
    String(t.id) === String(id) &&
    (t.dia||'').toLowerCase() === (dia||'').toLowerCase() &&
    (t.horario||'').trim() === (horario||'').trim()
  ) || null
})

// Flatpickr config (mesma regra do formulário)
const dateConfig = computed(() => {
  const base = {
    dateFormat: 'Y-m-d',
    altInput: true,
    altFormat: 'd/m/Y',
    locale: Portuguese,
    defaultDate: novaData.value || null
  }
  if (!turmaSelecionada.value) return base
  const diaDesejado = convertDayNameToNumber(turmaSelecionada.value.dia)
  if (diaDesejado == null) return base
  return {
    ...base,
    disable: [ (date) => date.getDay() !== diaDesejado ]
  }
})

// // Validação mínima
// const valid = computed(() => !!(nivelId.value && turmaSelecionada.value && novaData.value))

// Validação do formulário (campos obrigatórios + data no mesmo dia da turma)
const formValid = computed(() => {
  if (loading.value || isSubmitting.value) return false
  if (!nivelId.value) return false
  if (!turmaSelecionada.value) return false
  if (!novaData.value) return false

  // garante que a data escolhida bate com o dia da semana da turma
  const picked = new Date(novaData.value + 'T00:00:00')
  if (isNaN(picked)) return false
  const diaTurma = convertDayNameToNumber(turmaSelecionada.value.dia)
  if (diaTurma == null) return false

  return picked.getDay() === diaTurma
})

// SALVAR por dentro do modal
async function salvar() {
  if (!formValid.value || !props.aula?.id) return
  isSubmitting.value = true
  errorMsg.value = ''
  try {
    const diaNum = convertDayNameToNumber(turmaSelecionada.value.dia)
    const payload = {
      data_aula: novaData.value,                         // YYYY-MM-DD
      nivel_id: Number(nivelId.value) || nivelId.value,
      turma_id: Number(turmaSelecionada.value.id) || turmaSelecionada.value.id,
      dia_semana: (diaNum ?? turmaSelecionada.value.dia),// envia número se possível
      horario: turmaSelecionada.value.horario,
      turma_completa: `Turma: ${turmaSelecionada.value.id} | Dia: ${turmaSelecionada.value.dia} | Hora: ${turmaSelecionada.value.horario} | Instrutor: ${turmaSelecionada.value.instrutor}`,
      observacoes: observacoes.value || null,
      nome_usuario: (userNameFromGlobal ?? '').toString().trim() || null
    }

    await axios.patch(
      `/operacional/aulas-reposicao/${props.aula.id}/reagendar`,
      payload
    )

    // emite eventos para o pai decidir recarregar
    emit('saved', { id: props.aula.id, ...payload })
    emitClose({ saved: true })
  } catch (e) {
    console.error('Erro ao reagendar aula:', e)
    errorMsg.value = 'Não foi possível salvar. Tente novamente.'
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
/* Layout compartilhado com o primeiro modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-container {
  background: #fff;
  width: 100%;
  max-width: 30%;
  height: auto;
  min-height: auto;
  overflow-y: auto;
  margin: 0 10px;
  border-radius: 0px;
}

/* Cabeçalho do modal */
.modal-header {
  background: #ccc;
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.turma {
  /* background: #ccc; */
  padding: 5px 0px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}


/* Espaço após o cabeçalho */
.spacer {
  margin-bottom: 20px;
}

/* Conteúdo do modal */
.modal-content {
  padding: 25px;
}

/* Seção e grupos de formulário */
.section-title {
  font-size: 0.9rem;
  font-weight: bold;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 10px;
  margin-left: 0px;
  margin-right: 0px;

}

.form-group label {
  font-weight: bold;
  margin-bottom: 5px;
  display: block;
}

.required {
  color: red;
}

.form-control {
  width: 100%;
  padding: 5px;
  border: 1px solid #ced4da;
  border-radius: 0px;
}

/* Botões */
.btn {
  /* padding: 6px 12px; */
  border: none;
  cursor: pointer;
  margin-right: 25px;
  margin-top: 15%;
}

.btn-primary {
  background: #007bff;
  color: #fff;
}

.btn-secondary {
  background: #6c757d;
  color: #fff;
}

/* Outras classes utilitárias */
.d-flex {
  display: flex;
}

.justify-content-end {
  justify-content: flex-end;
}

.me-2 {
  margin-right: 0.5rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

.mb-4 {
  margin-bottom: 1.5rem;
}

/* Outras classes utilizadas na estrutura */
.row {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.col {
  flex: 1;
  padding: 0 2px;
  min-width: 150px;
}

.close-icon {
  font-size: 1.5rem;
  cursor: pointer;
}
</style>
